/**
 * Owner portal V3 shell: the fixed 200 px left sidebar that replaces the
 * horizontal tab strip for owner accounts (COL-528).
 *
 * WHY A FIXED SIDEBAR AND A BODY OFFSET, not a layout wrapper. There is no
 * single owner shell to wrap. `AdminV2Layout` is mounted by ~20 pages, and the
 * owner property, room, apartment and account pages each mount `V2AdminNavbar`
 * directly — and both are shared with staff and operators, whose navigation
 * must not change. Wrapping every owner route instead would still miss the
 * legacy console (`/admin?tab=analytics`), which is where an owner's Analytics
 * and Settings actually live.
 *
 * So the sidebar takes itself out of flow and the page is pushed over by one
 * rule in index.css keyed on `data-owner-shell`, set on <body> while this is
 * mounted. Every owner surface is offset, nothing else is touched, and no page
 * needs to know the shell exists.
 *
 * The impersonation banner is `position: fixed` at the bottom of the viewport
 * (ImpersonationBanner.tsx, mounted globally in App.tsx) and is NOT offset by
 * the body padding, so it runs under the sidebar. The user block reserves room
 * for it rather than being covered by it — an admin acting as an owner would
 * otherwise lose the sign-out menu behind the amber bar.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useOwnerIdentity } from "@/hooks/useOwnerIdentity";
import {
  useProperties,
  useOwnerVisiblePropertyStates,
  ownerMustSign,
} from "@/hooks/v2/useProperties";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { hrefFor, isActive, type RouteItem } from "./V2AdminNavbar";

/** Prototype tokens, kept together so a restyle is one edit, not twenty. */
const C = {
  surface: "#F7F5F0",
  hairline: "#ECE7DD",
  hover: "#EFEBE3",
  petrol: "#0F4C4A",
  teal: "#2FB3AA",
  tealBright: "#4ECDC4",
  night: "#06201F",
  activeBg: "#E3EFED",
  itemText: "#344746",
  faint: "#7A8E8C",
  clay: "#C0714F",
  ink: "#1A2B2A",
} as const;

export const OWNER_SHELL_BODY_ATTR = "data-owner-shell";

/**
 * Marks <body> for the whole time the shell is mounted so index.css can offset
 * the page. A counter, not a boolean: React can mount the next instance before
 * unmounting the old one during a route transition, and a plain remove on
 * teardown would drop the offset mid-navigation and flash the page under the
 * sidebar.
 */
let shellMountCount = 0;

function useBodyOffset() {
  useEffect(() => {
    shellMountCount += 1;
    document.body.setAttribute(OWNER_SHELL_BODY_ATTR, "1");
    return () => {
      shellMountCount -= 1;
      if (shellMountCount <= 0) {
        shellMountCount = 0;
        document.body.removeAttribute(OWNER_SHELL_BODY_ATTR);
      }
    };
  }, []);
}

type Props = {
  /** Owner nav entries, already role- and flag-filtered by V2AdminNavbar. */
  items: RouteItem[];
  labelFor: (item: RouteItem) => string;
  /** Unread owner notifications; the badge is hidden at zero. */
  notificationCount: number;
  legacyDefaultTab: string;
};

export function OwnerPortalSidebar({
  items,
  labelFor,
  notificationCount,
  legacyDefaultTab,
}: Props) {
  useBodyOffset();
  const { t } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Closing on navigation is the drawer's job, not every link's: a <Link> that
  // routes to the page you are already on fires no location change, and a
  // per-link onClick would leave the drawer open on exactly that tap.
  const { pathname: here, search } = useLocation();
  useEffect(() => setDrawerOpen(false), [here, search]);

  const body = (
    <SidebarBody
      items={items}
      labelFor={labelFor}
      notificationCount={notificationCount}
      legacyDefaultTab={legacyDefaultTab}
      // The effect above cannot catch the contract CTA: it routes to
      // /admin/properties, so tapping it while already there fires no
      // location change and the drawer stays open over the very card it
      // sends you to. Harmless on desktop, where the drawer is never open.
      onNavigate={() => setDrawerOpen(false)}
    />
  );

  return (
    <>
      {/* Desktop: the fixed rail the page is offset for. */}
      <nav
        aria-label={t("owner.nav.aria", "Owner portal")}
        className="fixed left-0 top-0 z-40 hidden h-screen w-[200px] flex-col border-r px-3 pb-4 pt-[22px] md:flex"
        style={{ background: C.surface, borderColor: C.hairline }}
      >
        {body}
      </nav>

      {/* Narrow: a top bar that opens the same rail in a drawer. The page is
          NOT offset here (the media query in index.css stops at 768px), so
          this bar sits in flow above the content like the old strip did. */}
      <div
        className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b px-4 md:hidden"
        style={{ background: C.surface, borderColor: C.hairline }}
      >
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="-ml-2 rounded-[8px] p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("owner.nav.openMenu", "Menü öffnen")}
            >
              <Menu className="h-5 w-5" style={{ color: C.petrol }} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="flex w-[260px] flex-col border-r px-3 pb-4 pt-[22px]"
            style={{ background: C.surface, borderColor: C.hairline }}
          >
            <SheetTitle className="sr-only">
              {t("owner.nav.aria", "Owner portal")}
            </SheetTitle>
            {body}
          </SheetContent>
        </Sheet>
        <BrandWordmark className="h-5 w-auto" />
        {notificationCount > 0 && (
          <span
            className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[10px] font-bold text-white"
            style={{ background: C.petrol }}
          >
            {notificationCount}
            <span className="sr-only">
              {" "}
              {t("owner.nav.unreadSuffix", "unread")}
            </span>
          </span>
        )}
      </div>
    </>
  );
}

/**
 * Everything inside the rail: wordmark, section label, nav, spacer, contract
 * call-to-action, user block. Rendered twice — once in the fixed desktop rail
 * and once inside the narrow-screen drawer — so the two can never diverge.
 */
function SidebarBody({
  items,
  labelFor,
  notificationCount,
  legacyDefaultTab,
  onNavigate,
}: Props & { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { isImpersonating, user, isOwnerPortalOnly } = useAuth();

  /**
   * An owner who holds nothing at all — the first-run state the glow exists
   * for. Same query ContractCta runs, so React Query serves both from one
   * fetch; and the same id-or-nothing rule, because `useProperties` called
   * without a user id returns EVERY property in the database, which would
   * make this false for everyone.
   *
   * Held back until the query has SUCCEEDED: an empty list that has not loaded
   * yet (or failed, or is disabled while auth hydrates) would flash the glow at
   * an owner who has ten properties.
   */
  const { data: ownedProperties = [], isSuccess: propertiesLoaded } =
    useProperties(user?.id, isOwnerPortalOnly && !!user?.id);
  const needsFirstProperty =
    isOwnerPortalOnly && propertiesLoaded && ownedProperties.length === 0;

  return (
    <>
      <Link
        to="/"
        className="mb-[22px] block rounded-md px-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={t("owner.nav.homeTitle", "coliviq Startseite")}
      >
        <BrandWordmark className="h-5 w-auto" />
      </Link>

      <div
        className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: C.faint }}
      >
        {t("owner.nav.portfolio", "Portfolio")}
      </div>

      <ul className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(
            item,
            pathname,
            searchParams,
            legacyDefaultTab
          );
          const badge =
            item.badge === "ownerNotifications" ? notificationCount : 0;
          // Neel, 2026-10-07: a brand-new owner has nothing to look at and no
          // clue what to do first. Until they own a single property, the one
          // route out of an empty portal glows. It stops the moment they have
          // one — this is a first-run nudge, not a permanent decoration.
          const guiding = needsFirstProperty && item.slug === "properties";
          return (
            <li key={`${item.kind}-${item.slug}`}>
              <Link
                to={hrefFor(item)}
                aria-current={active ? "page" : undefined}
                className={`flex w-full items-center justify-between gap-2 rounded-[8px] px-3 py-[9px] text-left text-[13.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  guiding ? "owner-cta-glow" : ""
                }`}
                style={{
                  background: active ? C.activeBg : "transparent",
                  color: active ? C.petrol : C.itemText,
                  fontWeight: active ? 600 : 400,
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = C.hover;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <span>{labelFor(item)}</span>
                {badge > 0 && (
                  <span
                    className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[10px] font-bold text-white"
                    style={{ background: C.petrol }}
                  >
                    {badge}
                    <span className="sr-only">
                      {" "}
                      {t("owner.nav.unreadSuffix", "unread")}
                    </span>
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="flex-1" />

      <ContractCta onNavigate={onNavigate} />

      {/* The impersonation banner is fixed to the viewport and runs under this
          sidebar, so the user block is lifted clear of it while it is up. */}
      <div style={{ paddingBottom: isImpersonating ? 52 : 0 }}>
        <UserBlock />
      </div>
    </>
  );
}

/**
 * The teal call-to-action an owner sees while a property of theirs still has
 * no signed management contract. Soft pulsing glow, as in the prototype —
 * it is the one thing in the portal that is genuinely waiting on the owner.
 *
 * Shown only for `awaiting_contract`. A property in `awaiting_approval` is
 * waiting on CoLiviq, not on the owner, and must offer no sign button
 * anywhere (PRD acceptance).
 *
 * With several properties waiting it names the oldest and counts the rest —
 * PRD question 8's default. `useProperties` orders by title, so "oldest" has
 * to be taken from `created_at` rather than from the list's own order.
 *
 * Clicking it goes to "My properties" (Neel, 14 Oct) rather than opening the
 * signing flow directly: the owner lands on the pending property card, sees
 * which property and what else is there, and signs from its own button —
 * the one route into a legal document, now entirely on that card rather than
 * split between here and there.
 */
function ContractCta({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useLanguage();
  const { user, isOwnerPortalOnly } = useAuth();
  // `useProperties` only scopes to the owner when it is GIVEN the user id —
  // called without one it returns every property in the database.
  const { data: properties = [] } = useProperties(
    user?.id,
    isOwnerPortalOnly && !!user?.id
  );
  const { data: states } = useOwnerVisiblePropertyStates(isOwnerPortalOnly);

  if (!states?.size) return null;
  const waiting = properties
    .filter((p) => ownerMustSign(states.get(Number(p.id))))
    .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""));
  if (waiting.length === 0) return null;

  const oldest = waiting[0];
  const extra = waiting.length - 1;

  return (
    <Link
      to="/admin/properties"
      onClick={onNavigate}
      className="owner-cta-glow mb-3.5 block w-full rounded-[12px] px-3.5 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ background: C.teal, color: C.night }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = C.tealBright;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.teal;
      }}
    >
      <span className="block text-[12.5px] font-bold leading-[1.3]">
        {t("owner.nav.ctaContract", "Verwaltungsvertrag abschließen")} →
      </span>
      <span
        className="mt-[3px] block text-[11px]"
        style={{ color: "rgba(6,32,31,0.75)" }}
      >
        {oldest.title}
        {extra > 0 && (
          <>
            {" · "}
            {t("owner.nav.ctaMore", "+{n} weitere").replace(
              "{n}",
              String(extra)
            )}
          </>
        )}
      </span>
    </Link>
  );
}

/**
 * Name, role and a menu that opens ABOVE it: the owner's identity, then
 * Settings, Language and Sign out.
 *
 * Settings lives here now rather than in the nav — it is the one owner
 * destination that is about the account rather than the portfolio, and the
 * PRD moves the DE/EN switch down to join it.
 */
function UserBlock() {
  const { t, language, setLanguage } = useLanguage();
  const { signOut, isOwnerPortalOnly } = useAuth();
  const { displayName, initials, email, billingLines } =
    useOwnerIdentity(isOwnerPortalOnly);

  return (
    <div className="border-t pt-3.5" style={{ borderColor: C.hairline }}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-[10px] p-1.5 text-left transition-colors hover:bg-[#EFEBE3] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ background: C.petrol }}
              aria-hidden
            >
              {initials}
            </span>
            <span className="min-w-0">
              <span
                className="block truncate text-[13px] font-semibold"
                style={{ color: C.ink }}
              >
                {displayName}
              </span>
              <span className="block text-[11px]" style={{ color: C.faint }}>
                {t("owner.nav.roleOwner", "Eigentümer")}
              </span>
            </span>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="start"
          sideOffset={6}
          className="w-[236px] rounded-[14px] p-1.5"
        >
          <div
            className="border-b px-3 pb-2 pt-2.5"
            style={{ borderColor: C.hairline }}
          >
            <div className="text-[13px] font-semibold">{displayName}</div>
            <div className="text-[11.5px]" style={{ color: C.faint }}>
              {email}
            </div>
            {billingLines.length > 0 && (
              <div className="mt-1 text-[11.5px]" style={{ color: C.faint }}>
                {billingLines.join(" · ")}
              </div>
            )}
          </div>

          <DropdownMenuItem asChild className="rounded-[8px] text-[13px]">
            <Link to="/admin?tab=settings">
              {t("owner.nav.settings", "Einstellungen")}
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="flex justify-between rounded-[8px] text-[13px]"
            // The menu stays open: switching language re-renders this list, and
            // closing it would hide the result of the thing just clicked.
            onSelect={(e) => {
              e.preventDefault();
              setLanguage(language === "en" ? "de" : "en");
            }}
          >
            <span>{t("owner.nav.language", "Sprache")}</span>
            <span className="text-xs" style={{ color: C.faint }}>
              {language === "de" ? "Deutsch" : "English"}
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="rounded-[8px] text-[13px]"
            style={{ color: C.clay }}
            onSelect={() => void signOut()}
          >
            {t("owner.nav.signOut", "Abmelden")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
